<!--
  ThreadPage — Phase 4-6 / T33, Phase 4-7 / T35.

  Master layout for the thread route. Three top-level pieces:
    ConversationHeader  (full-width, sticky top)
    Thread + Composer   (left column on wide, full-width on narrow)
    CustomerContextPanel (right column on wide, collapsible toggle on narrow)

  Layout impeccable direction:
    - Container queries on the root (`container-type: inline-size`).
    - >= 720px: CSS Grid `[1fr 320px]` with header spanning both columns.
      Panel column scrolls internally; thread column has its own scroll.
    - 480-720px: single column; panel collapses behind a "Customer info ▾"
      toggle row inserted between header and thread. Expanded panel slides
      inline above the thread (max-height 50%).
    - < 480px: same as 480-720 with reduced toggle padding.

  Focus order (DOM): header -> thread -> composer -> panel. Wide layout uses
  `grid-template-areas` to put panel on the visual right while keeping DOM
  order intact.

  Data: ConversationHeader needs the conversation row + host-summary preview;
  we look up the conversation via the cached `conversationsList` query and
  read host-summary so the header can flip its title between matched name
  and "Unmatched contact" without an extra request.

  Phase 4-7 / T35: also hosts the CreateEntityModal + success toast. When the
  operator picks an entity type from the header's Create ▾ menu, we mount the
  modal with prefilled values derived from host-summary + last 3 messages.
  On success the modal closes, we post `openHostEntity` via the embed bridge,
  and surface a 3-second toast.
-->
<script lang="ts">
  import { untrack } from 'svelte';
  import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
  import Thread from '../components/Thread.svelte';
  import Composer from '../components/Composer.svelte';
  import ConversationHeader from '../components/ConversationHeader.svelte';
  import ConversationSummary from '../components/ConversationSummary.svelte';
  import CustomerContextPanel from '../components/CustomerContextPanel.svelte';
  // NotesPanel moved into CustomerContextPanel (right-panel reshuffle).
  import CreateEntityModal, {
    type FormField,
  } from '../components/CreateEntityModal.svelte';
  import {
    conversationDetail,
    conversationsList,
    conversationMessages,
    hostSummary,
    type HostSummary,
    type Message,
  } from '../lib/api/queries.js';
  import { markReadMutation, setConversationStatusMutation } from '../lib/api/mutations.js';
  import { session } from '../lib/stores/session.svelte.js';
  import { getBridge } from '../lib/embed/bridge-singleton.svelte.js';
  import { newEnvelopeId } from '@platform/embed-bridge';

  type Props = { conversationId: string };
  let { conversationId }: Props = $props();

  // M5.1 — mark this conversation read whenever the thread mounts or the
  // operator navigates to a different conversation. Fire-and-forget; the
  // mutation invalidates `['conversations']` on settle, so the unread chip
  // drops to zero on the next list refetch.
  //
  // `untrack` around the mutation store is load-bearing, not tidiness. The
  // effect used to read `$markRead` in its tracked scope, and that store
  // changes as the mutation runs — so firing it re-ran the effect, which
  // fired it again. Opening ONE conversation put the browser into an endless
  // request loop; the counter stopped at 27,000 with
  // ERR_INSUFFICIENT_RESOURCES before the tab gave up.
  //
  // The old comment claimed the mutation key deduped repeat calls. TanStack
  // does not dedupe mutations by key — only queries — and that belief is
  // what the loop rode on.
  const queryClient = useQueryClient();
  const markRead = $derived(createMutation(markReadMutation(queryClient, conversationId)));
  $effect(() => {
    // Track conversationId ONLY, so re-navigating re-fires and nothing else
    // does.
    const id = conversationId;
    if (!id) return;
    untrack(() => {
      void $markRead.mutateAsync().catch(() => {
        // Best-effort — a bad mark-read shouldn't toast the operator.
      });
    });
  });

  // The list row is the instant first paint; the detail query is the truth.
  //
  // Reading only from the list made this pane depend on the inbox's filter:
  // close a conversation and it leaves the open queue, taking `status` with
  // it — so the closed state could never render and the composer came back.
  // A thread opened from a push notification under an active tag filter had
  // the same hole.
  const listQuery = $derived(createQuery(conversationsList()));
  const detailQuery = $derived(createQuery(conversationDetail(conversationId)));
  const conversation = $derived(
    $detailQuery.data ?? $listQuery.data?.find((c) => c.id === conversationId) ?? null,
  );

  // Phase 8.1 / M8.1.7 — AI summary banner gating. We only show it (and
  // the underlying POST only fires) when the current operator is the
  // conversation's assignee. Reading from the cached list row instead
  // of an extra fetch keeps this dependency-free.
  const currentUserId = $derived(session.state.sso?.user?.id ?? null);
  const assigneeUserId = $derived(conversation?.assignee?.userId ?? null);

  // A finished conversation offers no reply box. Absent status counts as
  // OPEN — see the field's note in queries.ts; guessing CLOSED would take
  // the composer away from a live thread.
  const closed = $derived(conversation?.status === 'CLOSED');
  const statusMutation = $derived(
    createMutation(setConversationStatusMutation(queryClient, conversationId)),
  );
  let statusError = $state<string | null>(null);

  async function setStatus(next: 'OPEN' | 'CLOSED') {
    statusError = null;
    try {
      await $statusMutation.mutateAsync(next);
    } catch (err) {
      statusError =
        err instanceof Error
          ? err.message
          : next === 'OPEN'
            ? 'Could not reopen this conversation.'
            : 'Could not close this conversation.';
    }
  }

  // Host-summary lookup for the header's name + match treatment. The panel
  // also reads this — TanStack will dedupe on the same query key.
  const summaryQuery = $derived(createQuery(hostSummary(conversationId)));

  // Messages query — already cached for Thread; we re-read it here to build
  // the contextNote prefill.
  const messagesQuery = $derived(createQuery(conversationMessages(conversationId)));

  let panelOpen = $state(false);
  function togglePanel() {
    panelOpen = !panelOpen;
  }

  // ---- Create-entity modal + toast (T35) ---------------------------------

  type ModalState = {
    entityType: string;
    displayName: string;
    formSchema: FormField[];
  };
  let modal = $state<ModalState | null>(null);
  let toast = $state<{ label: string; entityType: string } | null>(null);
  let toastTimer: number | null = null;

  function handleCreateSelect(sel: {
    entityType: string;
    displayName: string;
    formSchema: unknown;
  }): void {
    modal = {
      entityType: sel.entityType,
      displayName: sel.displayName,
      formSchema: normalizeFormSchema(sel.formSchema),
    };
  }

  function handleCloseModal(): void {
    modal = null;
  }

  function handleModalSuccess(entity: {
    entityType: string;
    entityId: string;
    deepLink: string;
  }): void {
    const bridge = getBridge();
    if (bridge) {
      bridge.send({
        v: 1,
        type: 'openHostEntity',
        id: newEnvelopeId(),
        data: { entityType: entity.entityType, entityId: entity.entityId },
      });
    }
    toast = { label: entity.entityId, entityType: entity.entityType };
    modal = null;
    if (toastTimer !== null) window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toast = null;
      toastTimer = null;
    }, 3000);
  }

  function dismissToast(): void {
    if (toastTimer !== null) {
      window.clearTimeout(toastTimer);
      toastTimer = null;
    }
    toast = null;
  }

  function normalizeFormSchema(raw: unknown): FormField[] {
    if (!Array.isArray(raw)) return [];
    return raw.filter(isFormField);
  }
  function isFormField(v: unknown): v is FormField {
    if (typeof v !== 'object' || v === null) return false;
    const o = v as Record<string, unknown>;
    if (typeof o.name !== 'string' || typeof o.label !== 'string') return false;
    if (typeof o.required !== 'boolean') return false;
    const t = o.type;
    return t === 'text' || t === 'email' || t === 'phone' || t === 'textarea' || t === 'select';
  }

  // ---- Prefill derivation -------------------------------------------------

  /**
   * Look up a phone-ish or email-ish value from host summary fields by label
   * substring match. Returns '' if nothing matches.
   */
  function pickFieldByLabel(
    summary: HostSummary | undefined | null,
    needles: string[],
  ): string {
    const fields = summary?.summary?.fields ?? [];
    for (const f of fields) {
      const lower = f.label.toLowerCase();
      if (needles.some((n) => lower.includes(n))) return f.value;
    }
    return '';
  }

  const prefill = $derived<Record<string, unknown>>((() => {
    const s = $summaryQuery.data ?? null;
    const displayNameFromSummary = s?.summary?.displayName?.trim();
    const fallbackName = conversation?.customerName?.trim() || 'Unknown contact';
    const name = displayNameFromSummary || fallbackName;
    const email = pickFieldByLabel(s, ['email']);
    const phone = pickFieldByLabel(s, ['phone', 'mobile', 'tel']);
    return {
      name,
      displayName: name,
      fullName: name,
      email,
      phone,
    };
  })());

  /** FNV-1a → 0..359 — deterministic hue per entity-type label. */
  function hashHue(input: string): number {
    let h = 2166136261;
    for (let i = 0; i < input.length; i++) {
      h ^= input.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return Math.abs(h) % 360;
  }

  const contextNote = $derived<string>((() => {
    const msgs: Message[] = $messagesQuery.data ?? [];
    const last = msgs.slice(-3);
    if (last.length === 0) return '';
    const lines = last
      .map((m) => {
        const body = (m.text ?? '').trim();
        return body.length > 0 ? `- ${body}` : `- [${m.type}]`;
      })
      .join('\n');
    return `Last ${last.length} message${last.length === 1 ? '' : 's'}:\n${lines}`;
  })());
</script>

<section class="page">
  <ConversationHeader
    conversation={conversation}
    hostSummary={$summaryQuery.data ?? null}
    onCreateSelect={handleCreateSelect}
    onClose={() => void setStatus('CLOSED')}
    closing={$statusMutation.isPending}
  />

  <!--
    Narrow-mode toggle. Hidden on wide via @container query. The native button
    works fine without JS for narrow widths; on wide the toggle row is
    display:none so the panel is always visible in its grid cell.
  -->
  <div class="toggle-row">
    <button
      type="button"
      class="toggle"
      aria-expanded={panelOpen}
      aria-controls="customer-context-panel"
      onclick={togglePanel}
    >
      <span>Customer info</span>
      <span class="caret" aria-hidden="true">{panelOpen ? '▴' : '▾'}</span>
    </button>
  </div>

  <div class="thread-col">
    <ConversationSummary
      {conversationId}
      {assigneeUserId}
      {currentUserId}
    />
    <div class="thread-scroll">
      <Thread {conversationId} customerDisplayName={conversation?.customerName ?? null} />
    </div>
    {#if closed}
      <!-- The composer is removed, not disabled. A greyed-out reply box
           invites an operator to type and tells them afterwards. -->
      <div class="closed-bar" role="status">
        <p>This conversation is closed.</p>
        <button type="button" onclick={() => void setStatus('OPEN')} disabled={$statusMutation.isPending}>
          {$statusMutation.isPending ? 'Reopening…' : 'Reopen'}
        </button>
      </div>
      {#if statusError}
        <p class="closed-error" role="alert">{statusError}</p>
      {/if}
    {:else}
      <Composer
        {conversationId}
        channelType={conversation?.channelType ?? null}
        visitorId={conversation?.externalThreadRef ?? null}
      />
    {/if}
  </div>

  <div
    id="customer-context-panel"
    class="panel-col"
    class:open={panelOpen}
    aria-hidden={!panelOpen ? 'true' : null}
  >
    <CustomerContextPanel {conversationId} {conversation} />
  </div>
</section>

{#if modal}
  <CreateEntityModal
    entityType={modal.entityType}
    displayName={modal.displayName}
    formSchema={modal.formSchema}
    conversationId={conversationId}
    prefilledValues={prefill}
    prefilledContextNote={contextNote}
    onClose={handleCloseModal}
    onSuccess={handleModalSuccess}
  />
{/if}

{#if toast}
  <button
    type="button"
    class="toast"
    aria-label="Created {toast.entityType} {toast.label}. Click to dismiss."
    onclick={dismissToast}
  >
    <span
      class="toast-type"
      style="--type-fg: oklch(48% 0.16 {hashHue(toast.entityType)})"
    >{toast.entityType}</span>
    <span class="toast-dot" aria-hidden="true">·</span>
    <span class="toast-name">{toast.label}</span>
  </button>
{/if}

<style>
  /* Where the composer stands on a finished conversation. Quiet on purpose:
     nothing is wrong, the work is done. Keeps the composer's footprint so
     the message list does not jump when a thread is closed. */
  .closed-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border-top: 1px solid var(--widget-border, oklch(90% 0.006 250));
    background: var(--widget-bg-sunk, oklch(96% 0.004 250));
  }
  .closed-bar p {
    margin: 0;
    font-size: 13px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .closed-bar button {
    border: 1px solid var(--widget-border, oklch(90% 0.006 250));
    background: var(--widget-bg-elevated, oklch(99.5% 0.003 250));
    color: var(--widget-fg, oklch(30% 0.02 250));
    border-radius: var(--widget-radius-action, 5px);
    padding: 6px 12px;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }
  .closed-bar button:disabled {
    cursor: default;
    opacity: 0.6;
  }
  .closed-error {
    margin: 0;
    padding: 8px 16px;
    font-size: 12px;
    color: var(--widget-danger, oklch(55% 0.2 25));
  }

  .page {
    height: 100%;
    display: grid;
    /* FOUR areas, so four rows. There were three, which left `panel` on an
       implicit `auto` row — and an auto row sized to the panel's content beat
       the thread's `1fr`, squeezing the conversation down to a sliver the
       moment somebody opened Customer info. `minmax` gives the thread a floor
       it cannot be pushed below; the panel is capped by its own max-height
       and scrolls inside it. */
    grid-template-rows: auto auto minmax(160px, 1fr) auto;
    grid-template-columns: 1fr;
    grid-template-areas:
      'header'
      'toggle'
      'thread'
      'panel';
    background: var(--widget-bg, oklch(99% 0.003 250));
    color: var(--widget-fg, oklch(20% 0.01 250));
    container-type: inline-size;
    container-name: thread-page;
    overflow: hidden;
  }

  /* Header sits in row 1, full width. */
  :global(.page > header.bar) {
    grid-area: header;
  }

  .toggle-row {
    grid-area: toggle;
    padding: 0 14px;
    border-bottom: 1px solid var(--widget-border, oklch(92% 0.005 250));
    background: var(--widget-bg, oklch(99% 0.003 250));
  }
  .toggle {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 4px;
    background: none;
    border: none;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    cursor: pointer;
  }
  .toggle:hover {
    color: var(--widget-fg, oklch(20% 0.01 250));
  }
  .toggle:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 1px;
    border-radius: 6px;
  }
  .toggle .caret {
    font-size: 11px;
  }

  .thread-col {
    grid-area: thread;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .thread-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }

  .panel-col {
    grid-area: panel;
    min-height: 0;
    overflow: hidden;
    max-height: 0;
    opacity: 0;
    transform: translateY(-4px);
    transition:
      max-height 0ms linear 160ms,
      opacity 160ms cubic-bezier(0.22, 1, 0.36, 1),
      transform 160ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .panel-col.open {
    max-height: 50vh;
    /* Scrolls within its own row rather than growing until the thread has
       nowhere left to go. */
    overflow: auto;
    opacity: 1;
    transform: translateY(0);
    transition:
      max-height 0ms linear 0ms,
      opacity 160ms cubic-bezier(0.22, 1, 0.36, 1),
      transform 160ms cubic-bezier(0.22, 1, 0.36, 1);
    border-top: 1px solid var(--widget-border, oklch(92% 0.005 250));
  }

  /* Wide layout: two-column grid. Header spans both. Panel is always visible
     on the right at 320px. Hide the narrow toggle row. */
  @container thread-page (min-width: 720px) {
    .page {
      grid-template-rows: auto 1fr;
      grid-template-columns: 1fr 320px;
      grid-template-areas:
        'header header'
        'thread panel';
    }
    .toggle-row {
      display: none;
    }
    .panel-col {
      max-height: none;
      opacity: 1;
      transform: none;
      transition: none;
      border-top: none;
    }
  }

  /* Very narrow: reduce toggle horizontal padding. */
  @container thread-page (max-width: 479px) {
    .toggle-row {
      padding: 0 8px;
    }
  }

  /* ---- Toast (T34) ----------------------------------------------------- */

  .toast {
    position: fixed;
    bottom: 16px;
    right: 16px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 12px;
    background: var(--widget-bg, oklch(99% 0.003 250));
    color: var(--widget-fg, oklch(20% 0.01 250));
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    border-radius: var(--widget-radius, 10px);
    box-shadow:
      0 1px 2px oklch(20% 0.01 250 / 0.06),
      0 12px 32px oklch(20% 0.01 250 / 0.14);
    font-size: 12.5px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    z-index: 1100;
    animation: toast-in 200ms cubic-bezier(0.22, 1, 0.36, 1);
    max-width: min(360px, calc(100vw - 32px));
    text-align: left;
  }
  .toast:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 2px;
  }
  .toast-type {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: var(--type-fg, var(--widget-fg-muted, oklch(52% 0.012 250)));
  }
  .toast-dot {
    color: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 30%, transparent);
  }
  .toast-name {
    color: var(--widget-fg, oklch(20% 0.01 250));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
  @keyframes toast-in {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
</style>
